import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const DEFAULT_STATUSES = [
  { name: 'Open' },
  { name: 'In Progress' },
  { name: 'Need Approval' },
  { name: 'Closed' },
  { name: 'Reopen' },
];

const CategoryCreation = () => {
  const [name, setName] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [customStatus, setCustomStatus] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [categoryId, setCategoryId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const locationCategoryId = searchParams.get("categoryId") || null;
  const baselineRef = useRef(null); 

  const buildSubmitPayload = ({
    id = categoryId,
    n = name,
    d = description,
    s = statuses,
  } = {}) => ({
    id: id || null,
    name: n || '',
    description: d || '',
    status: (Array.isArray(s) ? s : []).map((st) => ({
      id: st.id || null,
      name: st.name || '',
    })),
  });

  useEffect(() => {
    const run = async () => {
      if (locationCategoryId) {
        setCategoryId(locationCategoryId);
        setLoading(true);
        try {
          const resp = await fetch(
            `${process.env.REACT_APP_API_TICKET_CATEGORY_LIST}?id=${locationCategoryId}`,
            {
              method: 'GET',
              headers: {
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
                'Content-Type': 'application/json',
              },
            }
          );
          const data = await resp.json();

          if (resp.ok && data.success) {
            const cat = data.data?.category || {};
            setName(cat.name || '');
            setStatuses(cat.statuses || []);
            setDescription(cat.description || '');

            baselineRef.current = buildSubmitPayload({
              id: locationCategoryId,
              n: cat.name || '',
              d: cat.description || '',
              s: cat.statuses || [],
            });
          } else {
            throw new Error(data?.success_message || 'Failed to fetch category details');
          }
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: err?.message || 'Failed to fetch category details',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        } finally {
          setLoading(false);
        }
      } else {
        setStatuses(DEFAULT_STATUSES);
        baselineRef.current = buildSubmitPayload({
          id: null,
          n: '',
          d: '',
          s: DEFAULT_STATUSES,
        });
      }
    };

    run();
  }, [locationCategoryId]); 

  const handleAddStatus = () => {
    const next = (customStatus || '').trim();
    if (!next) return;

    const exists = statuses.some((s) => (s.name || '').toLowerCase() === next.toLowerCase());
    if (exists) return;

    setStatuses([...statuses, { name: next }]);
    setCustomStatus('');
  };

  const handleRemoveStatus = (nameToRemove) => {
    setStatuses(statuses.filter((s) => s.name !== nameToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (statuses.length < 4) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'You must add at least 4 statuses before submitting.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    if (!description.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Description is required before submitting.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const newPayload = buildSubmitPayload({});
    const oldPayload = baselineRef.current || {};

    if (categoryId) {
      const { digest, html } = buildDiffPreview(oldPayload, newPayload);
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      const resp = await fetch(process.env.REACT_APP_API_CATEGORY_CREATE_TICKET, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
        body: JSON.stringify(newPayload),
      });
      const data = await resp.json();

      if (!(resp.ok && data.success)) {
        throw new Error(data?.success_message || 'Something went wrong!');
      }

      fireAudit({
        entity: 'ticket_category',
        affected_id: categoryId || null,
        action: categoryId ? 'update' : 'create',
        businessApi: process.env.REACT_APP_API_CATEGORY_CREATE_TICKET,
        businessMethod: 'POST',
        oldPayload,
        newPayload,
        section: 'Ticket',
        subSection: 'Category Creation',
        extra: { page: 'CategoryCreation' },
      });

      Swal.fire({
        icon: 'success',
        title: categoryId ? 'Category Updated!' : 'Category Created!',
        text: data?.success_message || 'Success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      baselineRef.current = newPayload;

      setName('');
      setStatuses([]);
      setDescription('');
      navigate(-1);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err?.message || 'Failed to save category.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/ticketcategory" className="breadcrumb-link">
          Category List
        </Link>{' '}
        &gt; {categoryId ? 'Update Category' : 'Category Creation'}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="floating-select-form">
          <input
            id="category-name"
            name="name"
            className={`walkin-form ${name ? 'has-value' : ''}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            required
          />
          {name && <label htmlFor="category-name">Name</label>}
        </div>

        <div className="floating-select-form">
          <textarea
            id="category-description"
            name="description"
            className={`walkin-form-comments ${description ? 'has-value' : ''}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a Description"
            rows="4"
            required
          />
          {description && <label htmlFor="category-description">Description</label>}
        </div>

        <div style={{ overflow: 'scroll' }}>
          <fieldset className="enrollment-form-fieldset enrollment-form-mt-3" style={{ marginTop: 0 }}>
            <legend className="enrollment-form-legend">Status</legend>
            <div className="academy-rule-input-group-container">
              <input
                type="text"
                value={customStatus}
                onChange={(e) => setCustomStatus(e.target.value)}
                placeholder="Enter Status"
                className="walkin-form"
                disabled={loading}
              />
              <button
                type="button"
                className="plusButton"
                onClick={handleAddStatus}
                title="Add Status"
                disabled={loading || !customStatus.trim()}
              >
                <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                  <g>
                    <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
                  </g>
                </svg>
              </button>
            </div>
            <ul className="academy-rules-display-list">
              {statuses.map((status) => (
                <li
                  key={`${status.id || 'new'}-${status.name}`}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>{status.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveStatus(status.name)}
                    style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '12px', background: 'white', border: 'none' }}
                    title="Remove status"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="red"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z"
                      />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </fieldset>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? (categoryId ? 'Updating…' : 'Creating…') : (categoryId ? 'Update Category' : 'Create Category')}
        </button>
      </form>
    </div>
  );
};

export default CategoryCreation;
